import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { isPresenter, isStaff, isVerifiedSchoolStaff } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  FEATURE_REGISTRY,
  getActiveFeatures
} from '../../utils/userFeatureAccess';
import { resolveProgrammeListingCollection } from '../../utils/resolveProgrammeListingCollection';
import { useNavScrollReveal } from '../../hooks/useNavScrollReveal';
import {
  dismissGuestJupasLoginHint,
  GUEST_JUPAS_LOGIN_HINT_EVENT,
  shouldShowGuestJupasLoginHint,
} from '../../utils/guestJupasLoginHint';

const HOVER_CLOSE_MS = 180;

const ChevronDown = ({ open, className = '' }) => (
    <svg
        className={`block h-4 w-4 shrink-0 self-center transition-transform duration-200 ease-out motion-reduce:transition-none ${open ? 'rotate-180' : ''} ${className}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

function buildDesktopLinkDropdown({
    containerRef,
    open,
    onEnter,
    onLeave,
    onToggle,
    label,
    links,
    menuActive,
    isPromo,
    defaultDesktopNavBlockClass,
    desktopDropdownPanelClass,
    desktopDropdownListClass,
    promoDropdownPanelClass,
    dropdownLinkClass,
    closeDesktopDropdowns,
    chevronClassName = '',
}) {
    const triggerClass = isPromo
        ? `text-xs px-2 py-1 rounded-md ${menuActive ? 'text-white font-semibold' : 'text-white/85 hover:text-white'}`
        : defaultDesktopNavBlockClass(menuActive);

    return (
        <div
            className="relative inline-flex items-center"
            ref={containerRef}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            <button
                type="button"
                onClick={onToggle}
                className={`font-roboto inline-flex items-center gap-1 font-medium leading-snug min-h-6 box-border cursor-pointer transition-[color,background-color,border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A0C4FF] ${triggerClass}`}
                aria-expanded={open}
                aria-haspopup="true"
            >
                {label}
                <ChevronDown open={open} className={chevronClassName} />
            </button>
            <div
                className={desktopDropdownPanelClass(open)}
                role="menu"
                aria-hidden={!open}
                onMouseEnter={isPromo ? onEnter : undefined}
                onMouseLeave={isPromo ? onLeave : undefined}
            >
                <div className={isPromo ? `${promoDropdownPanelClass} w-max min-w-[13rem] ${desktopDropdownListClass}` : `w-max min-w-[13rem] rounded-md border border-edge bg-surface-elevated shadow-lg ${desktopDropdownListClass}`}>
                    {links.map((link) => (
                        <Link
                            key={link.to}
                            to={link.to}
                            role="menuitem"
                            className={dropdownLinkClass(link.to)}
                            onClick={closeDesktopDropdowns}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

function buildMobileLinkDropdown({
    open,
    setOpen,
    label,
    links,
    menuActive,
    mobileDropdownLinkClass,
    onNavigate,
}) {
    return (
        <div className="mb-4">
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                className={`flex w-full items-center justify-between text-left font-medium font-roboto text-sm cursor-pointer ${menuActive ? 'text-link' : 'text-ink-muted'}`}
                aria-expanded={open}
            >
                {label}
                <ChevronDown open={open} className="text-ink-subtle" />
            </button>
            <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
                <div className="overflow-hidden">
                    <div className="mt-2 flex flex-col gap-0 px-1.5">
                        {links.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={mobileDropdownLinkClass(link.to)}
                                onClick={onNavigate}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Surname first (Chinese convention); preferred name (nickname) in parentheses when both legal names exist. */
function formatAccountNameChinese(user) {
    if (!user) return { full: '', alias: '' };
    const last = (user.Last_Name || '').trim();
    const first = (user.First_Name || '').trim();
    const nick = (user.Nickname || '').trim();
    const legal = [last, first].filter(Boolean).join(' ');
    let full = '';
    if (legal && nick) {
        full = `${legal} (${nick})`;
    } else if (legal) {
        full = legal;
    } else if (nick) {
        full = nick;
    } else {
        full = user.User_ID || '';
    }
    const alias = legal && nick ? nick : '';
    return { full, alias };
}

function AccountNameWithOverflow({ full, alias, className = '' }) {
    const containerRef = useRef(null);
    const measureRef = useRef(null);
    const [showAlias, setShowAlias] = useState(false);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!full || !alias || !container || !measure) {
            setShowAlias(false);
            return undefined;
        }
        const check = () => {
            const overflow = measure.scrollWidth > container.clientWidth + 0.5;
            setShowAlias(overflow);
        };
        check();
        const ro = new ResizeObserver(check);
        ro.observe(container);
        return () => ro.disconnect();
    }, [full, alias]);

    const display = showAlias && alias ? alias : full;

    return (
        <div ref={containerRef} className={`min-w-0 relative ${className}`}>
            {full && alias ? (
                <span
                    ref={measureRef}
                    className="invisible absolute left-0 top-0 whitespace-nowrap pointer-events-none font-medium text-sm"
                    aria-hidden
                >
                    {full}
                </span>
            ) : null}
            <p className="font-medium text-ink truncate min-w-0 text-sm" title={full || undefined}>
                {display || '—'}
            </p>
        </div>
    );
}

/** Compact login | register pill shared by promotional and default nav bars. */
const LoginRegisterPill = forwardRef(function LoginRegisterPill(
    { isPromo, t, onNavigate, onDismissHint },
    ref
) {
    const pillSurface = isPromo
        ? 'bg-white text-[#121212]'
        : 'bg-[#121212] text-white dark:bg-white dark:text-[#121212]';
    const linkTone = isPromo
        ? 'text-[#121212]'
        : 'text-white dark:text-[#121212]';
    const linkHover = isPromo
        ? 'hover:bg-black/5'
        : 'hover:bg-white/10 dark:hover:bg-black/5';
    const separatorTone = isPromo ? 'text-[#121212]/40' : 'text-white/40 dark:text-[#121212]/40';
    const focusRing = isPromo
        ? 'focus-visible:ring-[#A0C4FF]'
        : 'focus-visible:ring-ring';
    const linkClass = `px-3 py-2 no-underline cursor-pointer ${linkTone} ${linkHover} transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset ${focusRing}`;

    const handleNavigate = () => {
        onDismissHint?.();
        onNavigate?.();
    };

    return (
        <span ref={ref} className="relative inline-flex">
            <span className={`inline-flex items-center rounded-full text-sm font-semibold overflow-hidden ${pillSurface}`}>
                <Link to="/login" className={linkClass} onClick={handleNavigate}>
                    {t('common.login')}
                </Link>
                <span className={`${separatorTone} select-none`} aria-hidden>|</span>
                <Link to="/register" className={linkClass} onClick={handleNavigate}>
                    {t('common.register')}
                </Link>
            </span>
        </span>
    );
});

function useGuestLoginHint(isGuest) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!isGuest) {
            setOpen(false);
            return undefined;
        }

        const syncHint = (event) => {
            if (event.detail?.dismissed) {
                setOpen(false);
                return;
            }
            setOpen(shouldShowGuestJupasLoginHint());
        };

        setOpen(shouldShowGuestJupasLoginHint());
        window.addEventListener(GUEST_JUPAS_LOGIN_HINT_EVENT, syncHint);
        return () => window.removeEventListener(GUEST_JUPAS_LOGIN_HINT_EVENT, syncHint);
    }, [isGuest]);

    const dismissHint = useCallback(() => {
        dismissGuestJupasLoginHint();
        setOpen(false);
    }, []);

    return { open, dismissHint };
}

function GuestLoginHintBubble({ open, anchorRef, mobileAnchorRef, isPromo, t, onDismiss }) {
    const [position, setPosition] = useState(null);

    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return undefined;
        }

        const updatePosition = () => {
            const useDesktopAnchor = window.matchMedia('(min-width: 768px)').matches;
            const anchor = (useDesktopAnchor ? anchorRef : mobileAnchorRef).current;
            if (!anchor) {
                setPosition(null);
                return;
            }
            const rect = anchor.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 8,
                right: Math.max(12, window.innerWidth - rect.right),
            });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, anchorRef, mobileAnchorRef]);

    if (!open || !position || typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <div
            role="status"
            className={`fixed z-[120] w-[min(18rem,calc(100vw-1.5rem))] rounded-lg border px-3 py-2.5 text-left text-xs leading-relaxed shadow-lg ${
                isPromo
                    ? 'border-white/20 bg-[#1f1f1f] text-white'
                    : 'border-edge bg-surface-elevated text-ink shadow-black/20'
            }`}
            style={{ top: position.top, right: position.right }}
        >
            <span
                className={`absolute -top-1.5 right-6 h-3 w-3 rotate-45 border-l border-t ${
                    isPromo
                        ? 'border-white/20 bg-[#1f1f1f]'
                        : 'border-edge bg-surface-elevated'
                }`}
                aria-hidden
            />
            <div className="relative flex items-start gap-2">
                <p className="flex-1">{t('jupa.guestLoginHint')}</p>
                <button
                    type="button"
                    onClick={onDismiss}
                    className={`shrink-0 rounded p-0.5 leading-none transition-colors focus:outline-none focus-visible:ring-2 ${
                        isPromo
                            ? 'text-white/70 hover:text-white focus-visible:ring-[#A0C4FF]'
                            : 'text-ink-subtle hover:text-ink focus-visible:ring-ring'
                    }`}
                    aria-label={t('common.close')}
                >
                    ×
                </button>
            </div>
        </div>,
        document.body
    );
}

const PROMO_PRIMARY = '#A0C4FF';
const PROMO_DARK = '#121212';

function NavBar({ isAuthenticated, userData, onLogout, variant = 'default' }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isDark } = useTheme();
    const isPromo = variant === 'promotional';
    const useMonoLogo = isPromo || isDark;
    const homePath = '/';
    const isTrajectoryPage = location.pathname === '/trajectory';
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [desktopAboutOpen, setDesktopAboutOpen] = useState(false);
    const [desktopBetaOpen, setDesktopBetaOpen] = useState(false);
    const [desktopLegacyOpen, setDesktopLegacyOpen] = useState(false);
    const [desktopAccountOpen, setDesktopAccountOpen] = useState(false);
    const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
    const [mobileBetaOpen, setMobileBetaOpen] = useState(false);
    const [mobileLegacyOpen, setMobileLegacyOpen] = useState(false);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [topProgrammeLabel, setTopProgrammeLabel] = useState(null);
    const isUserPresenter = isPresenter(userData);
    const isUserStaff = isStaff(userData);
    const isUserSchoolStaff = isVerifiedSchoolStaff(userData);
    const trialFeatureLinks = !isUserPresenter
        ? getActiveFeatures(userData)
            .map((key) => FEATURE_REGISTRY[key])
            .filter(Boolean)
            .map((feature) => ({ to: feature.path, label: t(feature.labelKey) }))
        : [];
    const showStaffTestResults = isUserStaff && !isUserPresenter;
    const showSchoolDashboard = isUserSchoolStaff && !isUserPresenter;
    const isGuest = !isAuthenticated;
    const guestLoginHint = useGuestLoginHint(isGuest);
    const loginPillRef = useRef(null);
    const mobileMenuButtonRef = useRef(null);

    const anyDesktopDropdownOpen = desktopAboutOpen || desktopBetaOpen || desktopLegacyOpen || desktopAccountOpen;
    const navScrollHidden = useNavScrollReveal({
        suspend: isMobileMenuOpen || anyDesktopDropdownOpen || languageMenuOpen || guestLoginHint.open,
    });

    const aboutRef = useRef(null);
    const betaRef = useRef(null);
    const legacyRef = useRef(null);
    const accountRef = useRef(null);
    const aboutLeaveTimer = useRef(null);
    const betaLeaveTimer = useRef(null);
    const legacyLeaveTimer = useRef(null);
    const accountLeaveTimer = useRef(null);

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const aboutSectionActive = isActive('/hiring') || isActive('/team') || isActive('/press-releases');

    const { betaNavLinks, legacyNavLinks } = useMemo(() => {
        if (!isUserPresenter) {
            return { betaNavLinks: [], legacyNavLinks: [] };
        }

        const betaNavLinks = [
            { to: '/beta/eddy', label: t('navigation.eddy') },
            { to: '/ability-test', label: t('navigation.takeTest') },
            { to: '/interest-test', label: t('navigation.interestTest') },
            { to: '/test-results', label: t('navigation.testResults') },
            { to: '/jupasurvey-results', label: t('navigation.jupasSurveyResults') },
            { to: '/analytics', label: t('navigation.analytics') },
            { to: '/bug-reports', label: t('navigation.bugReports') }
        ];

        const legacyNavLinks = [];
        if (isAuthenticated) {
            legacyNavLinks.push({ to: '/eddy', label: t('navigation.eddy') });
            legacyNavLinks.push({ to: '/trajectory', label: t('navigation.trajectory') });
        }
        legacyNavLinks.push(
            { to: '/beta/jupaselect', label: t('navigation.jupaSelectBeta') },
            { to: '/presentation', label: t('navigation.presentation') },
            { to: '/beta/schools', label: t('navigation.schoolAdmin') },
            { to: '/beta/feature-invites', label: t('navigation.featureInvites') },
            { to: '/school', label: t('navigation.schoolDashboard') },
            { to: '/beta/promo-home', label: t('navigation.promotionalHomeLegacy') }
        );

        return { betaNavLinks, legacyNavLinks };
    }, [isAuthenticated, isUserPresenter, t]);

    const canShowPresenterMenus = isUserPresenter && (betaNavLinks.length > 0 || legacyNavLinks.length > 0);

    const betaMenuActive = useMemo(
        () => betaNavLinks.some((link) => isActive(link.to)),
        [betaNavLinks, location.pathname]
    );

    const legacyMenuActive = useMemo(
        () => legacyNavLinks.some((link) => isActive(link.to)),
        [legacyNavLinks, location.pathname]
    );

    const defaultDesktopNavBlockClass = (active) =>
        `text-xs xl:text-sm px-1.5 xl:px-2 py-1 rounded-full border transition-[color,background-color,border-color,box-shadow] cursor-pointer ${
            active
                ? 'text-link font-semibold border-link bg-accent/25 shadow-sm'
                : 'text-ink hover:text-link hover:bg-surface-muted border-transparent shadow-none'
        }`;

    const defaultDesktopDropdownItemClass = (active) =>
        `block w-full box-border whitespace-nowrap font-roboto font-medium text-sm px-3 py-2 rounded-full border transition-[color,background-color,border-color] duration-150 cursor-pointer ${
            active
                ? 'border-link bg-accent/25 text-link font-semibold'
                : 'border-transparent text-ink hover:border-transparent hover:text-link hover:bg-surface-muted'
        }`;

    const desktopDropdownListClass = 'px-1.5 py-1';

    /** One shared row height so plain links and chevron controls line up. */
    const desktopLinkClass = (path) => {
        const active = isActive(path);
        if (isPromo) {
            return `font-roboto font-medium text-xs leading-snug inline-flex items-center min-h-6 whitespace-nowrap rounded-md px-2 py-1 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                active ? 'text-white font-semibold' : 'text-white/85 hover:text-white'
            }`;
        }
        return `font-roboto font-medium leading-snug inline-flex items-center min-h-6 box-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${defaultDesktopNavBlockClass(active)}`;
    };

    const mobileLinkClass = (path) => {
        const active = isActive(path);
        return `block font-medium text-sm mb-4 cursor-pointer ${active ? 'text-link font-semibold' : 'text-ink-muted hover:text-link'}`;
    };

    const mobileDropdownLinkClass = (path) => {
        const active = isActive(path);
        return `block w-full box-border text-sm font-roboto px-3 py-2 rounded-full border transition-[color,background-color,border-color] cursor-pointer ${
            active
                ? 'border-link bg-accent/25 text-link font-semibold'
                : 'border-transparent text-ink-muted hover:text-link hover:bg-surface-muted'
        }`;
    };

    const promoNavHighlightClass = (path, { hiddenOnMobile = false } = {}) => {
        const active = isActive(path);
        const visibility = hiddenOnMobile ? 'hidden sm:inline-flex' : 'inline-flex';
        return `${visibility} items-center px-3 py-1.5 text-xs font-semibold rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#121212] focus-visible:ring-[#A0C4FF] whitespace-nowrap ${
            active ? 'ring-2 ring-white/40' : 'text-white/85 hover:text-white'
        }`;
    };

    const promoNavHighlightStyle = (path) =>
        isActive(path) ? { backgroundColor: PROMO_PRIMARY, color: PROMO_DARK } : undefined;

    const aboutTriggerClass = aboutSectionActive
        ? isPromo
            ? 'text-white font-semibold'
            : defaultDesktopNavBlockClass(true)
        : isPromo
            ? 'text-white/85 hover:text-white border-transparent'
            : defaultDesktopNavBlockClass(false);

    const promoDropdownPanelClass =
        'rounded-xl border border-white/15 bg-[#121212]/90 backdrop-blur-xl shadow-lg shadow-black/30';

    const dropdownLinkClass = (path) => {
        const active = isActive(path);
        if (isPromo) {
            return `block font-roboto font-medium text-sm px-3 py-2 border-l-4 transition-colors duration-150 cursor-pointer ${
                active
                    ? 'border-[#A0C4FF] text-white font-semibold bg-white/10'
                    : 'border-transparent text-white/85 hover:border-white/30 hover:text-white hover:bg-white/5'
            }`;
        }
        return defaultDesktopDropdownItemClass(active);
    };

    const clearAboutTimer = useCallback(() => {
        if (aboutLeaveTimer.current) {
            clearTimeout(aboutLeaveTimer.current);
            aboutLeaveTimer.current = null;
        }
    }, []);

    const clearBetaTimer = useCallback(() => {
        if (betaLeaveTimer.current) {
            clearTimeout(betaLeaveTimer.current);
            betaLeaveTimer.current = null;
        }
    }, []);

    const clearLegacyTimer = useCallback(() => {
        if (legacyLeaveTimer.current) {
            clearTimeout(legacyLeaveTimer.current);
            legacyLeaveTimer.current = null;
        }
    }, []);

    const clearAccountTimer = useCallback(() => {
        if (accountLeaveTimer.current) {
            clearTimeout(accountLeaveTimer.current);
            accountLeaveTimer.current = null;
        }
    }, []);

    const closeDesktopDropdowns = () => {
        setDesktopAboutOpen(false);
        setDesktopBetaOpen(false);
        setDesktopLegacyOpen(false);
        setDesktopAccountOpen(false);
        clearAboutTimer();
        clearBetaTimer();
        clearLegacyTimer();
        clearAccountTimer();
    };

    useEffect(() => {
        closeDesktopDropdowns();
    }, [location.pathname]);

    useEffect(() => {
        if (canShowPresenterMenus) return;
        setDesktopBetaOpen(false);
        setDesktopLegacyOpen(false);
        setMobileBetaOpen(false);
        setMobileLegacyOpen(false);
    }, [canShowPresenterMenus]);

    useEffect(() => {
        const onDoc = (e) => {
            if (aboutRef.current && !aboutRef.current.contains(e.target)) {
                setDesktopAboutOpen(false);
            }
            if (betaRef.current && !betaRef.current.contains(e.target)) {
                setDesktopBetaOpen(false);
            }
            if (legacyRef.current && !legacyRef.current.contains(e.target)) {
                setDesktopLegacyOpen(false);
            }
            if (accountRef.current && !accountRef.current.contains(e.target)) {
                setDesktopAccountOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === 'Escape') closeDesktopDropdowns();
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [clearAboutTimer, clearBetaTimer, clearLegacyTimer]);

    useEffect(() => () => {
        clearAboutTimer();
        clearBetaTimer();
        clearLegacyTimer();
        clearAccountTimer();
    }, [clearAboutTimer, clearBetaTimer, clearLegacyTimer, clearAccountTimer]);

    useEffect(() => {
        let cancelled = false;
        async function loadTopProgramme() {
            if (!isAuthenticated || !userData?.jupas_programme_order || Object.keys(userData.jupas_programme_order).length === 0) {
                setTopProgrammeLabel(null);
                return;
            }
            const orderEntries = Object.entries(userData.jupas_programme_order);
            orderEntries.sort(([, posA], [, posB]) => posA - posB);
            const code = orderEntries[0]?.[0];
            if (!code) {
                setTopProgrammeLabel(null);
                return;
            }
            setTopProgrammeLabel(code);
            try {
                const collection = await resolveProgrammeListingCollection();
                if (!collection) {
                    throw new Error('no programme listing collection');
                }
                const response = await fetch(`/api/programmes/${collection}/codes`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ codes: [code] })
                });
                if (!response.ok) throw new Error('programmes fetch failed');
                const result = await response.json();
                if (!result.success) throw new Error(result.message || 'programmes');
                const p = result.programmes_by_code[code];
                if (cancelled) return;
                const lang = i18n.language || 'en';
                if (p) {
                    const title =
                        lang === 'tc' || lang === 'sc'
                            ? p.full_title_cn || p.full_title_en || p.short_name || code
                            : p.full_title_en || p.full_title_cn || p.short_name || code;
                    setTopProgrammeLabel(title);
                } else {
                    setTopProgrammeLabel(code);
                }
            } catch {
                if (!cancelled) setTopProgrammeLabel(code);
            }
        }
        loadTopProgramme();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, userData?.User_ID, userData?.jupas_programme_order, i18n.language]);

    const handleAboutEnter = () => {
        clearAboutTimer();
        setDesktopAboutOpen(true);
        setDesktopBetaOpen(false);
        setDesktopLegacyOpen(false);
        setDesktopAccountOpen(false);
        clearBetaTimer();
        clearLegacyTimer();
        clearAccountTimer();
    };

    const handleAboutLeave = () => {
        clearAboutTimer();
        aboutLeaveTimer.current = setTimeout(() => setDesktopAboutOpen(false), HOVER_CLOSE_MS);
    };

    const handleBetaEnter = () => {
        if (!canShowPresenterMenus || betaNavLinks.length === 0) return;
        clearBetaTimer();
        setDesktopBetaOpen(true);
        setDesktopAboutOpen(false);
        setDesktopLegacyOpen(false);
        setDesktopAccountOpen(false);
        clearAboutTimer();
        clearLegacyTimer();
        clearAccountTimer();
    };

    const handleBetaLeave = () => {
        if (!canShowPresenterMenus || betaNavLinks.length === 0) return;
        clearBetaTimer();
        betaLeaveTimer.current = setTimeout(() => setDesktopBetaOpen(false), HOVER_CLOSE_MS);
    };

    const handleLegacyEnter = () => {
        if (!canShowPresenterMenus || legacyNavLinks.length === 0) return;
        clearLegacyTimer();
        setDesktopLegacyOpen(true);
        setDesktopAboutOpen(false);
        setDesktopBetaOpen(false);
        setDesktopAccountOpen(false);
        clearAboutTimer();
        clearBetaTimer();
        clearAccountTimer();
    };

    const handleLegacyLeave = () => {
        if (!canShowPresenterMenus || legacyNavLinks.length === 0) return;
        clearLegacyTimer();
        legacyLeaveTimer.current = setTimeout(() => setDesktopLegacyOpen(false), HOVER_CLOSE_MS);
    };

    const handleAccountEnter = () => {
        clearAccountTimer();
        setDesktopAccountOpen(true);
        setDesktopAboutOpen(false);
        setDesktopBetaOpen(false);
        setDesktopLegacyOpen(false);
        clearAboutTimer();
        clearBetaTimer();
        clearLegacyTimer();
    };

    const handleAccountLeave = () => {
        clearAccountTimer();
        accountLeaveTimer.current = setTimeout(() => setDesktopAccountOpen(false), HOVER_CLOSE_MS);
    };

    const handleLogout = () => {
        onLogout();
        navigate(homePath);
        setIsMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((o) => {
            if (o) {
                setMobileAboutOpen(false);
                setMobileBetaOpen(false);
                setMobileLegacyOpen(false);
            }
            return !o;
        });
    };

    const promoDropdownZ = 'z-[200]';

    const desktopDropdownPanelClass = (open) =>
        `absolute left-0 top-full pt-1 w-max min-w-[13rem] origin-top transition-all duration-200 ease-out motion-reduce:transition-none ${
            isPromo ? promoDropdownZ : 'z-[60]'
        } ${
            open
                ? 'pointer-events-auto visible translate-y-0 scale-100 opacity-100'
                : 'pointer-events-none invisible -translate-y-1 scale-[0.98] opacity-0'
        }`;

    const desktopAccountDropdownPanelClass = (open) =>
        `absolute right-0 top-full pt-1 min-w-[15rem] max-w-[18rem] origin-top-right transition-all duration-200 ease-out motion-reduce:transition-none ${
            isPromo ? promoDropdownZ : 'z-[60]'
        } ${
            open
                ? 'pointer-events-auto visible translate-y-0 scale-100 opacity-100'
                : 'pointer-events-none invisible -translate-y-1 scale-[0.98] opacity-0'
        }`;

    const accountMenuActive = isActive('/view-report');
    const { full: accountNameFull, alias: accountNameAlias } = formatAccountNameChinese(userData);
    const accountForm = userData?.Form ? String(userData.Form).trim() : '';

    const desktopAboutMenu = (
        <div
            className="relative inline-flex items-center"
            ref={aboutRef}
            onMouseEnter={handleAboutEnter}
            onMouseLeave={handleAboutLeave}
        >
            <button
                type="button"
                onClick={() => {
                    setDesktopAboutOpen((o) => !o);
                    setDesktopBetaOpen(false);
                    setDesktopLegacyOpen(false);
                }}
                className={`font-roboto inline-flex items-center gap-1 font-medium leading-snug min-h-6 box-border cursor-pointer transition-[color,background-color,border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A0C4FF] ${
                    isPromo
                        ? `text-xs px-2 py-1 rounded-md ${aboutTriggerClass}`
                        : aboutTriggerClass
                }`}
                aria-expanded={desktopAboutOpen}
                aria-haspopup="true"
            >
                {t('navigation.about')}
                <ChevronDown open={desktopAboutOpen} className={isPromo ? 'text-white/80' : ''} />
            </button>
            <div
                className={desktopDropdownPanelClass(desktopAboutOpen)}
                role="menu"
                aria-hidden={!desktopAboutOpen}
                onMouseEnter={isPromo ? handleAboutEnter : undefined}
                onMouseLeave={isPromo ? handleAboutLeave : undefined}
            >
                <div className={isPromo ? `${promoDropdownPanelClass} w-max min-w-[13rem] ${desktopDropdownListClass}` : `w-max min-w-[13rem] rounded-md border border-edge bg-surface-elevated shadow-lg ${desktopDropdownListClass}`}>
                    <Link
                        to="/team"
                        role="menuitem"
                        className={dropdownLinkClass('/team')}
                        onClick={closeDesktopDropdowns}
                    >
                        {t('navigation.meetTheTeam')}
                    </Link>
                    <Link
                        to="/press-releases"
                        role="menuitem"
                        className={dropdownLinkClass('/press-releases')}
                        onClick={closeDesktopDropdowns}
                    >
                        {t('navigation.pressReleases')}
                    </Link>
                    <Link
                        to="/hiring"
                        role="menuitem"
                        className={dropdownLinkClass('/hiring')}
                        onClick={closeDesktopDropdowns}
                    >
                        {t('navigation.hiring')}
                    </Link>
                </div>
            </div>
        </div>
    );

    const mobileAboutMenu = (
        <div className="mb-4">
            <button
                type="button"
                onClick={() => setMobileAboutOpen((o) => !o)}
                className={`flex w-full items-center justify-between text-left font-medium font-roboto text-sm cursor-pointer ${aboutSectionActive ? 'text-link' : 'text-ink-muted'
                    }`}
                aria-expanded={mobileAboutOpen}
            >
                {t('navigation.about')}
                <ChevronDown open={mobileAboutOpen} className="text-ink-subtle" />
            </button>
            <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${mobileAboutOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
            >
                <div className="overflow-hidden">
                    <div className="mt-2 flex flex-col gap-0 px-1.5">
                        <Link
                            to="/team"
                            className={mobileDropdownLinkClass('/team')}
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {t('navigation.meetTheTeam')}
                        </Link>
                        <Link
                            to="/press-releases"
                            className={mobileDropdownLinkClass('/press-releases')}
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {t('navigation.pressReleases')}
                        </Link>
                        <Link
                            to="/hiring"
                            className={mobileDropdownLinkClass('/hiring')}
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            {t('navigation.hiring')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );

    const desktopBetaMenu = betaNavLinks.length > 0
        ? buildDesktopLinkDropdown({
            containerRef: betaRef,
            open: desktopBetaOpen,
            onEnter: handleBetaEnter,
            onLeave: handleBetaLeave,
            onToggle: () => {
                setDesktopBetaOpen((open) => !open);
                setDesktopAboutOpen(false);
                setDesktopLegacyOpen(false);
            },
            label: t('navigation.beta'),
            links: betaNavLinks,
            menuActive: betaMenuActive,
            isPromo,
            defaultDesktopNavBlockClass,
            desktopDropdownPanelClass,
            desktopDropdownListClass,
            promoDropdownPanelClass,
            dropdownLinkClass,
            closeDesktopDropdowns,
            chevronClassName: isPromo ? 'text-white/80' : '',
        })
        : null;

    const desktopLegacyMenu = legacyNavLinks.length > 0
        ? buildDesktopLinkDropdown({
            containerRef: legacyRef,
            open: desktopLegacyOpen,
            onEnter: handleLegacyEnter,
            onLeave: handleLegacyLeave,
            onToggle: () => {
                setDesktopLegacyOpen((open) => !open);
                setDesktopAboutOpen(false);
                setDesktopBetaOpen(false);
            },
            label: t('navigation.legacy'),
            links: legacyNavLinks,
            menuActive: legacyMenuActive,
            isPromo,
            defaultDesktopNavBlockClass,
            desktopDropdownPanelClass,
            desktopDropdownListClass,
            promoDropdownPanelClass,
            dropdownLinkClass,
            closeDesktopDropdowns,
            chevronClassName: isPromo ? 'text-white/80' : '',
        })
        : null;

    const mobileBetaMenu = betaNavLinks.length > 0
        ? buildMobileLinkDropdown({
            open: mobileBetaOpen,
            setOpen: setMobileBetaOpen,
            label: t('navigation.beta'),
            links: betaNavLinks,
            menuActive: betaMenuActive,
            mobileDropdownLinkClass,
            onNavigate: () => setIsMobileMenuOpen(false),
        })
        : null;

    const mobileLegacyMenu = legacyNavLinks.length > 0
        ? buildMobileLinkDropdown({
            open: mobileLegacyOpen,
            setOpen: setMobileLegacyOpen,
            label: t('navigation.legacy'),
            links: legacyNavLinks,
            menuActive: legacyMenuActive,
            mobileDropdownLinkClass,
            onNavigate: () => setIsMobileMenuOpen(false),
        })
        : null;

    const navBarRow = (
        <>
                        <div className={`flex min-w-0 flex-1 items-center ${isPromo ? 'overflow-visible' : ''}`}>
                            <div className="relative z-10 shrink-0">
                                <Link to={homePath} className="font-roboto font-medium text-base flex items-center leading-none rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A0C4FF]">
                                    <img
                                        src={useMonoLogo ? '/asset/icons/logo-mono.svg' : '/asset/icons/logo-v3.png'}
                                        alt="Company logo"
                                        className={
                                            isPromo
                                                ? 'h-9 w-9 sm:h-10 sm:w-10'
                                                : isDark
                                                    ? 'h-8 w-8 sm:h-9 sm:w-9'
                                                    : 'h-10 w-10 sm:h-12 sm:w-12'
                                        }
                                    />
                                </Link>
                            </div>
                            {isActive('/jupas-calculator') || isActive('/jupas-survey') || isActive('/jupas-results') ? (
                                <h1
                                    className={`pointer-events-none absolute left-1/2 top-1/2 min-w-0 -translate-x-1/2 -translate-y-1/2 truncate px-1 text-center text-sm font-bold tracking-normal md:hidden ${
                                        isPromo ? 'text-white' : 'text-ink'
                                    }`}
                                    style={{ width: 'calc(100% - 9rem)' }}
                                >
                                    {isActive('/jupas-results')
                                        ? t('navigation.jupasResults')
                                        : isActive('/jupas-survey')
                                            ? t('navigation.jupasSurvey')
                                            : t('navigation.jupaSelect')}
                                </h1>
                            ) : null}
                            <div className={`hidden md:flex md:items-center ${
                                isPromo
                                    ? 'md:ml-4 md:space-x-3 lg:ml-6 lg:space-x-4 min-w-0 overflow-visible'
                                    : 'md:ml-4 md:space-x-1 lg:ml-5 lg:space-x-2 xl:ml-10 xl:space-x-4'
                            }`}>
                                <Link
                                    to={homePath}
                                    className={isPromo ? promoNavHighlightClass('/') : desktopLinkClass('/')}
                                    style={isPromo ? promoNavHighlightStyle('/') : undefined}
                                >
                                    {t('navigation.home')}
                                </Link>

                                {trialFeatureLinks.map((link) => (
                                    <Link key={link.to} to={link.to} className={desktopLinkClass(link.to)}>
                                        {link.label}
                                    </Link>
                                ))}

                                <Link
                                    to="/jupas-calculator"
                                    className={
                                        isPromo
                                            ? promoNavHighlightClass('/jupas-calculator', { hiddenOnMobile: true })
                                            : desktopLinkClass('/jupas-calculator')
                                    }
                                    style={isPromo ? promoNavHighlightStyle('/jupas-calculator') : undefined}
                                >
                                    {isPromo ? t('promotionalHome.jupasCalculator') : t('navigation.jupaSelect')}
                                </Link>
                                <Link
                                    to="/jupas-survey"
                                    className={isPromo ? promoNavHighlightClass('/jupas-survey', { hiddenOnMobile: true }) : desktopLinkClass('/jupas-survey')}
                                    style={isPromo ? promoNavHighlightStyle('/jupas-survey') : undefined}
                                >
                                    {t('navigation.jupasSurvey')}
                                </Link>
                                <Link
                                    to="/jupas-results"
                                    className={isPromo ? promoNavHighlightClass('/jupas-results', { hiddenOnMobile: true }) : desktopLinkClass('/jupas-results')}
                                    style={isPromo ? promoNavHighlightStyle('/jupas-results') : undefined}
                                >
                                    {t('navigation.jupasResults')}
                                </Link>
                                {desktopAboutMenu}
                                {canShowPresenterMenus && desktopBetaMenu}
                                {canShowPresenterMenus && desktopLegacyMenu}
                            </div>
                        </div>
                        <div className="hidden md:flex shrink-0 items-center text-sm">
                            <div className="flex items-center gap-2 sm:gap-3">
                                <LanguageSwitcher
                                    compact
                                    darkSurface={isPromo}
                                    onOpenChange={setLanguageMenuOpen}
                                />
                                {isAuthenticated ? (
                                    <>
                                        <button
                                            type="button"
                                            className={isPromo ? 'text-white/85 hover:text-white' : 'text-ink-muted hover:text-link'}
                                            aria-label={t('navigation.notifications')}
                                        >
                                            <i className="fas fa-bell text-xl"></i>
                                        </button>
                                        <div
                                            className="relative inline-flex items-center"
                                            ref={accountRef}
                                            onMouseEnter={handleAccountEnter}
                                            onMouseLeave={handleAccountLeave}
                                        >
                                            <Link
                                                to="/view-report"
                                                className={`font-roboto inline-flex items-center gap-1 font-medium leading-snug min-h-6 box-border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                                    isPromo
                                                        ? `rounded-md transition-colors text-xs px-2 py-1 whitespace-nowrap ${accountMenuActive ? 'text-white font-semibold' : 'text-white/85 hover:text-white'}`
                                                        : defaultDesktopNavBlockClass(accountMenuActive)
                                                }`}
                                                aria-expanded={desktopAccountOpen}
                                                aria-haspopup="true"
                                                onClick={() => {
                                                    closeDesktopDropdowns();
                                                }}
                                            >
                                                {t('common.myAccount')}
                                                <ChevronDown open={desktopAccountOpen} className={isPromo ? 'text-white/80' : 'text-ink-subtle'} />
                                            </Link>
                                            <div
                                                className={desktopAccountDropdownPanelClass(desktopAccountOpen)}
                                                role="region"
                                                aria-hidden={!desktopAccountOpen}
                                                onMouseEnter={isPromo ? handleAccountEnter : undefined}
                                                onMouseLeave={isPromo ? handleAccountLeave : undefined}
                                            >
                                                <div
                                                    className={
                                                        isPromo
                                                            ? `${promoDropdownPanelClass} py-3 px-3 text-sm text-white/90`
                                                            : 'rounded-md border border-edge bg-surface-elevated py-3 px-3 shadow-lg text-sm text-ink'
                                                    }
                                                >
                                                    <div className="space-y-2">
                                                        <div
                                                            className={
                                                                accountForm
                                                                    ? 'grid grid-cols-2 gap-x-4 gap-y-1 items-start'
                                                                    : ''
                                                            }
                                                        >
                                                            <div className="min-w-0">
                                                                <p className={`text-xs font-medium ${isPromo ? 'text-white/55' : 'text-ink-subtle'}`}>{t('report.name')}</p>
                                                                <div className={isPromo ? '[&_p]:!text-white' : ''}>
                                                                    <AccountNameWithOverflow full={accountNameFull} alias={accountNameAlias} />
                                                                </div>
                                                            </div>
                                                            {accountForm ? (
                                                                <div className="min-w-0 text-right">
                                                                    <p className={`text-xs font-medium ${isPromo ? 'text-white/55' : 'text-ink-subtle'}`}>{t('report.form')}</p>
                                                                    <p className={`font-medium break-words ${isPromo ? 'text-white' : 'text-ink'}`}>{accountForm}</p>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        {topProgrammeLabel ? (
                                                            <div>
                                                                <p className={`text-xs font-medium ${isPromo ? 'text-white/55' : 'text-ink-subtle'}`}>{t('report.topProgramme')}</p>
                                                                <p className={`font-medium break-words leading-snug ${isPromo ? 'text-white' : 'text-ink'}`}>{topProgrammeLabel}</p>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <Link
                                                        to="/edit-profile"
                                                        className={
                                                            isPromo
                                                                ? 'mt-3 block text-center text-xs font-medium text-[#A0C4FF] hover:text-white no-underline'
                                                                : 'mt-3 block text-center text-xs font-medium text-link hover:text-link-hover no-underline'
                                                        }
                                                        onClick={closeDesktopDropdowns}
                                                    >
                                                        {t('navigation.editProfile')}
                                                    </Link>
                                                    {showStaffTestResults ? (
                                                        <Link
                                                            to="/test-results"
                                                            className={
                                                                isPromo
                                                                    ? 'mt-2 block text-center text-xs font-medium text-[#A0C4FF] hover:text-white no-underline'
                                                                    : 'mt-2 block text-center text-xs font-medium text-link hover:text-link-hover no-underline'
                                                            }
                                                            onClick={closeDesktopDropdowns}
                                                        >
                                                            {t('navigation.testResults')}
                                                        </Link>
                                                    ) : null}
                                                    {showSchoolDashboard ? (
                                                        <Link
                                                            to="/school"
                                                            className={
                                                                isPromo
                                                                    ? 'mt-2 block text-center text-xs font-medium text-[#A0C4FF] hover:text-white no-underline'
                                                                    : 'mt-2 block text-center text-xs font-medium text-link hover:text-link-hover no-underline'
                                                            }
                                                            onClick={closeDesktopDropdowns}
                                                        >
                                                            {t('navigation.schoolDashboard')}
                                                        </Link>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        onClick={handleLogout}
                                                        className={
                                                            isPromo
                                                                ? 'mt-3 pt-3 w-full border-t border-white/15 text-center text-xs font-medium text-red-400 hover:text-red-300'
                                                                : 'mt-3 pt-3 w-full border-t border-edge text-center text-xs font-medium text-danger hover:text-danger/80'
                                                        }
                                                    >
                                                        {t('common.logout')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <LoginRegisterPill
                                        ref={loginPillRef}
                                        isPromo={isPromo}
                                        t={t}
                                        onDismissHint={guestLoginHint.dismissHint}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="relative z-10 flex shrink-0 items-center gap-1 md:hidden">
                            <LanguageSwitcher compact darkSurface={isPromo} onOpenChange={setLanguageMenuOpen} />
                            <button
                                ref={mobileMenuButtonRef}
                                type="button"
                                onClick={toggleMobileMenu}
                                className={`inline-flex items-center justify-center p-2 rounded-md cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A0C4FF] ${
                                    isPromo ? 'text-white hover:text-white/80' : 'text-ink-muted hover:text-link'
                                }`}
                            >
                                <span className="sr-only">{t('navigation.openMainMenu')}</span>
                                {!isMobileMenuOpen ? (
                                    <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                ) : (
                                    <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </button>
                        </div>
        </>
    );

    return (
        <>
            {isPromo ? (
                <div
                    className={`fixed top-2.5 sm:top-3 left-0 right-0 z-[60] px-2.5 sm:px-3 pointer-events-none transition-transform duration-300 ease-out motion-reduce:transition-none ${
                        navScrollHidden
                            ? '-translate-y-[calc(100%+0.6rem)] sm:-translate-y-[calc(100%+0.8rem)]'
                            : 'translate-y-0'
                    }`}
                >
                    <div className={`relative mx-auto w-full max-w-6xl overflow-visible ${navScrollHidden ? 'pointer-events-none' : 'pointer-events-auto'}`}>
                        <nav
                            className="relative overflow-visible rounded-2xl sm:rounded-3xl border border-white/15 shadow-lg shadow-black/20 backdrop-blur-xl transition-all duration-300 ease-out"
                            style={{ backgroundColor: 'rgba(18, 18, 18, 0.72)' }}
                            aria-label={t('promotionalHome.navLabel')}
                        >
                            <div className="overflow-visible px-4 sm:px-6 lg:px-8">
                                <div className="relative flex h-14 sm:h-16 items-center justify-between gap-2 overflow-visible text-sm">
                                    {navBarRow}
                                </div>
                            </div>
                        </nav>
                    </div>
                </div>
            ) : (
                <nav
                    className={`fixed top-0 left-0 right-0 overflow-visible bg-surface border-b border-edge shadow-sm z-50 transition-transform duration-300 ease-out motion-reduce:transition-none ${
                        navScrollHidden ? '-translate-y-full pointer-events-none' : 'translate-y-0'
                    } ${isTrajectoryPage ? 'w-full' : ''}`}
                >
                    <div className={`overflow-visible ${isTrajectoryPage ? 'w-full' : 'max-w-7xl'} mx-auto px-4 sm:px-6 lg:px-8`}>
                        <div className="relative flex h-16 items-center justify-between gap-2 overflow-visible text-sm">
                            {navBarRow}
                        </div>
                    </div>
                </nav>
            )}

            <div className={`fixed inset-0 z-[100] ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300 ease-in-out`}>
                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={toggleMobileMenu}></div>
                <div className={`absolute right-0 top-0 h-full w-72 max-w-[85vw] bg-surface shadow-lg transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} z-[101]`}>
                    <div className="flex flex-col h-full">
                        <div className="flex items-start justify-between p-4 border-b border-edge gap-2">
                            <div className="flex flex-col gap-2 min-w-0">
                                <span className="text-xs font-semibold text-ink-subtle uppercase tracking-wide">{t('navigation.accountMenu')}</span>
                                {isAuthenticated ? (
                                    <div className="flex flex-col gap-2 text-sm min-w-0">
                                        <Link
                                            to="/view-report"
                                            className="text-link font-medium hover:underline"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {t('common.myAccount')}
                                        </Link>
                                        <div className="text-xs text-ink-muted space-y-1 pl-0 border-l-2 border-edge pl-2 min-w-0">
                                            <div
                                                className={
                                                    accountForm
                                                        ? 'grid grid-cols-2 gap-x-3 gap-y-1 items-start'
                                                        : ''
                                                }
                                            >
                                                <div className="min-w-0">
                                                    <p className="text-ink-subtle">{t('report.name')}</p>
                                                    <AccountNameWithOverflow full={accountNameFull} alias={accountNameAlias} />
                                                </div>
                                                {accountForm ? (
                                                    <div className="min-w-0 text-right">
                                                        <p className="text-ink-subtle">{t('report.form')}</p>
                                                        <p className="font-medium text-ink break-words">{accountForm}</p>
                                                    </div>
                                                ) : null}
                                            </div>
                                            {topProgrammeLabel ? (
                                                <p className="break-words leading-snug">
                                                    <span className="text-ink-subtle">{t('report.topProgramme')}: </span>
                                                    {topProgrammeLabel}
                                                </p>
                                            ) : null}
                                        </div>
                                        <Link
                                            to="/edit-profile"
                                            className={`block text-sm font-medium ${
                                                isActive('/edit-profile')
                                                    ? 'text-link font-semibold'
                                                    : 'text-ink-muted hover:text-link'
                                            }`}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {t('navigation.editProfile')}
                                        </Link>
                                        {showStaffTestResults ? (
                                            <Link
                                                to="/test-results"
                                                className={`block text-sm font-medium ${
                                                    isActive('/test-results')
                                                        ? 'text-link font-semibold'
                                                        : 'text-ink-muted hover:text-link'
                                                }`}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                                {t('navigation.testResults')}
                                            </Link>
                                        ) : null}
                                        {showSchoolDashboard ? (
                                            <Link
                                                to="/school"
                                                className={`block text-sm font-medium ${
                                                    isActive('/school')
                                                        ? 'text-link font-semibold'
                                                        : 'text-ink-muted hover:text-link'
                                                }`}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                            >
                                                {t('navigation.schoolDashboard')}
                                            </Link>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={handleLogout}
                                            className="mt-3 pt-3 w-full border-t border-edge text-left text-sm font-medium text-danger hover:text-danger/80"
                                        >
                                            {t('common.logout')}
                                        </button>
                                    </div>
                                ) : (
                                    <LoginRegisterPill
                                        isPromo={isPromo}
                                        t={t}
                                        onDismissHint={guestLoginHint.dismissHint}
                                        onNavigate={() => setIsMobileMenuOpen(false)}
                                    />
                                )}
                            </div>
                            <button type="button" onClick={toggleMobileMenu} className="text-ink-subtle hover:text-ink shrink-0 p-1" aria-label={t('navigation.closeMenu')}>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 text-sm">
                            <Link to={homePath} className={mobileLinkClass('/')} onClick={() => setIsMobileMenuOpen(false)}>{t('navigation.home')}</Link>

                            {trialFeatureLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={mobileLinkClass(link.to)}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            ))}

                            <Link to="/jupas-calculator" className={mobileLinkClass('/jupas-calculator')} onClick={() => setIsMobileMenuOpen(false)}>{t('navigation.jupaSelect')}</Link>
                            <Link to="/jupas-survey" className={mobileLinkClass('/jupas-survey')} onClick={() => setIsMobileMenuOpen(false)}>{t('navigation.jupasSurvey')}</Link>
                            <Link to="/jupas-results" className={mobileLinkClass('/jupas-results')} onClick={() => setIsMobileMenuOpen(false)}>{t('navigation.jupasResults')}</Link>
                            {mobileAboutMenu}
                            {canShowPresenterMenus && mobileBetaMenu}
                            {canShowPresenterMenus && mobileLegacyMenu}

                        </div>
                    </div>
                </div>
            </div>
            {isGuest ? (
                <GuestLoginHintBubble
                    open={guestLoginHint.open}
                    anchorRef={loginPillRef}
                    mobileAnchorRef={mobileMenuButtonRef}
                    isPromo={isPromo}
                    t={t}
                    onDismiss={guestLoginHint.dismissHint}
                />
            ) : null}
        </>
    );
}

export default NavBar;
